import React, { useState } from 'react';
import { ClipboardCheck, ChevronRight, TrendingUp, Calendar, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useRelationshipAssessment } from '@/hooks/useRelationshipAssessment';
import { usePartnerLink } from '@/hooks/usePartnerLink';
import { RelationshipAssessmentForm } from './RelationshipAssessmentForm';
import { AssessmentResults } from './AssessmentResults';
import { PartnerAssessmentView } from './PartnerAssessmentView';
import { format, parseISO, differenceInDays } from 'date-fns';
import { getScoreInterpretation } from '@/data/relationshipAssessmentQuestions';

interface RelationshipAssessmentCardProps {
  relationshipType: 'romantic' | 'family' | 'friendship' | 'work' | 'other';
}

export function RelationshipAssessmentCard({ relationshipType }: RelationshipAssessmentCardProps) {
  const [showAssessment, setShowAssessment] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  const { assessments, isLoading, getLatestAssessment } = useRelationshipAssessment();
  const { isLinked, partnerLink } = usePartnerLink();
  
  const latestAssessment = getLatestAssessment();
  const previousAssessment = assessments.length > 1 ? assessments[1] : null;
  
  const daysSinceLastAssessment = latestAssessment 
    ? differenceInDays(new Date(), parseISO(latestAssessment.assessment_date))
    : null;

  const shouldRetake = !latestAssessment || daysSinceLastAssessment! >= 30;

  const handleComplete = () => {
    setShowAssessment(false);
    setShowResults(true);
  };

  const relationshipLabel = {
    romantic: 'Romantic Relationship',
    family: 'Family',
    friendship: 'Friendship',
    work: 'Work Relationship',
    other: 'Relationship'
  }[relationshipType];

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="h-32" />
      </Card>
    );
  }

  return (
    <>
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <ClipboardCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Relationship Assessment</CardTitle>
                <CardDescription>
                  Take a snapshot of your {relationshipLabel.toLowerCase()}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isLinked && (
                <Badge variant="outline" className="text-xs">
                  <Users className="w-3 h-3 mr-1" />
                  Linked
                </Badge>
              )}
              {latestAssessment && (
                <Badge variant="secondary" className="text-xs">
                  {daysSinceLastAssessment === 0 
                    ? 'Today' 
                    : `${daysSinceLastAssessment}d ago`}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {latestAssessment ? (
            <>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Overall Health</p>
                  <p 
                    className="text-2xl font-bold"
                    style={{ 
                      color: getScoreInterpretation(
                        Math.round((latestAssessment.relationship_health_score || 0) / 100 * 21)
                      ).color 
                    }}
                  >
                    {Math.round(latestAssessment.relationship_health_score || 0)}%
                  </p>
                </div>
                {previousAssessment && (
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">vs previous</p>
                    {(() => {
                      const diff = (latestAssessment.relationship_health_score || 0) - 
                                   (previousAssessment.relationship_health_score || 0);
                      if (Math.abs(diff) < 2) return (
                        <p className="text-sm text-muted-foreground">Stable</p>
                      );
                      return (
                        <p className={`text-sm font-medium ${diff > 0 ? 'text-accent' : 'text-primary'}`}>
                          {diff > 0 ? '+' : ''}{Math.round(diff)}%
                        </p>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowResults(true)}
                >
                  View Results
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                {shouldRetake && (
                  <Button 
                    className="flex-1"
                    onClick={() => setShowAssessment(true)}
                  >
                    <TrendingUp className="w-4 h-4 mr-1" />
                    Retake
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Understand your relationship's strengths and growth areas through 
                research-backed questions. Takes about 5 minutes.
              </p>
              <Button 
                className="w-full"
                onClick={() => setShowAssessment(true)}
              >
                Start Assessment
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}

          {/* Partner's Assessment View */}
          {isLinked && relationshipType === 'romantic' && (
            <div className="mt-4 pt-4 border-t">
              <PartnerAssessmentView 
                relationshipType={relationshipType} 
                userAssessment={latestAssessment}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assessment Form Dialog */}
      <Dialog open={showAssessment} onOpenChange={setShowAssessment}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
          <RelationshipAssessmentForm
            relationshipType={relationshipType}
            onComplete={handleComplete}
            onCancel={() => setShowAssessment(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={showResults} onOpenChange={setShowResults}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {latestAssessment && (
            <AssessmentResults
              assessment={latestAssessment}
              previousAssessment={previousAssessment}
              relationshipType={relationshipType}
              onRetake={() => {
                setShowResults(false);
                setShowAssessment(true);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
